/**
 * Hosted Cognito UI social login **callback**.
 *
 * Routed at **`/social-login`**. Expects OAuth `code` and `state` in the URL
 * and completes sign-in via `authUtil.exchangeAuthorizationCodeForAccessToken()`.
 *
 * Do not confuse with {@link ../CustomSocialAuth/CustomSocialAuth} (`/social-auth`), which handles custom IdP
 * redirect plus one-time `consume_token` against `/api/user/session`.
 */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { authUtil } from '../../SharedLib/Util/AuthUtil';
import { logging } from '../../SharedLib/Util/LoggingUtil';
import Alert from '../../SharedLib/Component/Alert/Alert';
import Button from '../../SharedLib/Component/Buttons/Button/Button';
import LoadingBox from '../../SharedLib/Component/LoadingBox/LoadingBox';
import styles from './SocialLogin.module.scss';
import { getCookie } from '../../SharedLib/Util/CookieUtil';
import Spinner from './Spinner/Spinner';
import { safeRedirect } from '../../SharedLib/Util/RedirectUtil';
import { removeCookie } from '../../SharedLib';

const SocialLogin: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | JSX.Element | React.ReactNode | null>(null);
  const history = useHistory();
  useEffect(() => {
    (window as any).zE('webWidget', 'hide');
    const queryParams = new URLSearchParams(window.location.search);

    const completeLogin = async () => {
      try {
        const accessToken = getCookie('accessToken');
        if (accessToken) {
          setError('You are already logged in');
          await new Promise(resolve => setTimeout(resolve, 3000));
          window.close();
          return;
        }
        const result = await authUtil.exchangeAuthorizationCodeForAccessToken();
        if (!result.success) {
          setError(
            result.error.code === 'MISSING_ATTRIBUTES_REQUIRED' ? (
              <span>
                It looks like your account profile is missing either your first or last name.
                <br />
                <br />
                To fix this, go to the account settings of the service you're using to log in (for example Google, Facebook etc.), add your
                first and last name, and then try log in again.
              </span>
            ) : (
              result.error.msg || 'Failed to complete login'
            ),
          );
          setLoading(false);
          return;
        }
        setLoading(false);
        window.close();

        const redirectUrl = getCookie('socialLoginRedirectUrl');
        if (redirectUrl) {
          removeCookie('socialLoginRedirectUrl');
          safeRedirect(redirectUrl);
        } else {
          safeRedirect('https://w3schools.com');
        }
      } catch (err) {
        logging.logError('Social login error:', err);
        setError('An unexpected error occurred during login');
        setLoading(false);
      }
    };

    completeLogin();
  }, [history]);

  if (loading) {
    return (
      <div className={styles.social_login}>
        <div className={styles.modal_inner}>
          <LoadingBox
            position="fixed"
            top_displacement="auto"
            text="Just a moment, signing you in ..."
            textSize="20px"
            textWeight="normal"
            spinner={<Spinner />}
            text_placement="bottom"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.social_login}>
      <div className={styles.modal_inner}>
        <h1>{error ? 'Log In failed' : 'Done!'}</h1>

        {error ? (
          <>
            <div style={{ marginTop: '30px', marginBottom: '30px' }}>
              <Alert msg={error} type="warning" />
            </div>
            <div className={styles.cta_bottom_box}>
              <Button as="a" variant="primary" fullwidth={true} href="/login" text="Return to login" />
            </div>
          </>
        ) : (
          <div className={styles.cta_bottom_box}>
            <Button variant="primary" fullwidth={true} onClick={() => window.close()} text="Close this window" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialLogin;
